import { useEffect, useRef, useState } from "react";
import { Pipette } from "lucide-react";

const RECENT_KEY = "mb-recent-colours";
const RECENT_LIMIT = 10;
const HEX = /^#[0-9a-f]{6}$/i;

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

function hexToHsv(hex) {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const delta = max - Math.min(r, g, b);
  let h = 0;
  if (delta) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h = (h * 60 + 360) % 360;
  }
  return { h, s: max ? delta / max : 0, v: max };
}

function hsvToHex({ h, s, v }) {
  const f = (n) => {
    const k = (n + h / 60) % 6;
    return Math.round((v - v * s * Math.max(0, Math.min(k, 4 - k, 1))) * 255);
  };
  return `#${[f(5), f(3), f(1)].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

function readRecent() {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(stored) ? stored.filter((c) => HEX.test(c)) : [];
  } catch {
    return [];
  }
}

function remember(hex) {
  const next = [hex, ...readRecent().filter((c) => c !== hex)].slice(0, RECENT_LIMIT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Private mode or storage full: recents are a convenience only.
  }
  return next;
}

// onChange(hex, continuous): continuous is true while dragging, so the editor
// can fold a whole drag into one undo step.
export default function ColorPicker({ value, onChange, palettes = [], label = "Colour" }) {
  const [open, setOpen] = useState(false);
  const [hsv, setHsv] = useState(() => hexToHsv(HEX.test(value) ? value : "#000000"));
  const [draft, setDraft] = useState(value);
  const [recent, setRecent] = useState(readRecent);
  const svRef = useRef(null);
  const hasEyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

  // Follow outside changes (undo, another control) without losing the hue of greys.
  useEffect(() => {
    setDraft(value);
    if (HEX.test(value) && hsvToHex(hsv) !== value.toLowerCase()) setHsv(hexToHsv(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (hex) => {
    // Typing already applied the colour; only record a new undo step if it changed.
    if (hex !== value) onChange(hex, false);
    setRecent(remember(hex));
  };

  const updateHsv = (next, continuous) => {
    setHsv(next);
    const hex = hsvToHex(next);
    setDraft(hex);
    onChange(hex, continuous);
  };

  const pickSv = (event) => {
    const rect = svRef.current.getBoundingClientRect();
    updateHsv(
      {
        h: hsv.h,
        s: clamp((event.clientX - rect.left) / rect.width, 0, 1),
        v: clamp(1 - (event.clientY - rect.top) / rect.height, 0, 1),
      },
      true
    );
  };

  const pickFromScreen = async () => {
    try {
      const result = await new window.EyeDropper().open();
      const hex = result.sRGBHex.toLowerCase();
      if (HEX.test(hex)) {
        setHsv(hexToHsv(hex));
        commit(hex);
      }
    } catch {
      // Cancelled with Escape.
    }
  };

  const rows = [...palettes.filter((row) => row.colors?.length), { label: "Recent", colors: recent }].filter(
    (row) => row.colors.length
  );

  return (
    <div className="mb-cp">
      <div className="mb-cp-row">
        <button
          type="button"
          className="mb-cp-current"
          style={{ background: value }}
          onClick={() => setOpen((state) => !state)}
          aria-expanded={open}
          aria-label={`${label}: ${value}. ${open ? "Close" : "Open"} colour picker`}
        />
        <input
          type="text"
          value={draft}
          maxLength={7}
          spellCheck={false}
          aria-label={`${label} hex code`}
          onChange={(event) => {
            const next = event.target.value.trim();
            setDraft(next);
            if (HEX.test(next)) {
              setHsv(hexToHsv(next));
              onChange(next.toLowerCase(), true);
            }
          }}
          onBlur={() => (HEX.test(draft) ? commit(draft.toLowerCase()) : setDraft(value))}
          onKeyDown={(event) => event.key === "Enter" && event.currentTarget.blur()}
        />
        {hasEyeDropper && (
          <button type="button" className="mb-icon-button" onClick={pickFromScreen} title="Pick a colour from the screen" aria-label="Pick a colour from the screen">
            <Pipette size={15} strokeWidth={1.8} />
          </button>
        )}
      </div>

      {open && (
        <div className="mb-cp-panel">
          <div
            ref={svRef}
            className="mb-cp-sv"
            style={{ backgroundColor: `hsl(${hsv.h} 100% 50%)` }}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              pickSv(event);
            }}
            onPointerMove={(event) => event.buttons === 1 && pickSv(event)}
            onPointerUp={() => setRecent(remember(hsvToHex(hsv)))}
          >
            <span
              className="mb-cp-sv-cursor"
              style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: hsvToHex(hsv) }}
            />
          </div>
          <input
            type="range"
            className="mb-cp-hue"
            min={0}
            max={359}
            value={Math.round(hsv.h)}
            aria-label="Hue"
            onChange={(event) => updateHsv({ ...hsv, h: Number(event.target.value) }, true)}
            onPointerUp={() => setRecent(remember(hsvToHex(hsv)))}
          />
        </div>
      )}

      {rows.map((row) => (
        <div key={row.label} className="mb-cp-palette">
          <span>{row.label}</span>
          <div>
            {row.colors.map((colour) => (
              <button
                key={colour}
                type="button"
                className={colour === value ? "active" : ""}
                style={{ background: colour }}
                onClick={() => {
                  setHsv(hexToHsv(colour));
                  commit(colour);
                }}
                title={colour}
                aria-label={`Use ${colour}`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
